import {useAccessories} from '@api'
import {
  activeCell,
  formatCurrency,
  getUserData,
  isCompanyUser,
  isSuperAdmin,
  renderClickableCell,
  useViewNavigate,
} from '@common'
import {NavigationPathKey, TranslationKey} from '@enums'
import {GridColDef, GridColumnVisibilityModel} from '@mui/x-data-grid'
import {getCompanyCurrencyAtom, getUserSelectionAtom, pageLinksAtom} from '@state'

import {useEffect, useMemo} from 'react'

import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'

export interface AccessoryTableData {
  name: string
  products: number
  price: string
  active: boolean
}

export function useAccessorySearchModel() {
  const setTitleLinks = useSetAtom(pageLinksAtom)

  const {t} = useTranslation()
  const navigate = useViewNavigate()

  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const userSelection = userSelections[currentUser.id]

  const getCompanyCurrency = useAtomValue(getCompanyCurrencyAtom)
  const currency = userSelection ? getCompanyCurrency(userSelection?.companyId) : 'EUR'

  const {data: accessoriesData} = useAccessories()

  const rows = useMemo(() => {
    if (!accessoriesData) return []
    else
      return accessoriesData
        .filter(item => (isSuperAdmin() ? item.companyId === userSelection?.companyId || '' : true))
        .map(item => ({
          name: item.name,
          products: item.products.length,
          price: formatCurrency(item.price, currency),
          active: item.active,
          id: item.id,
        }))
  }, [accessoriesData])

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: TranslationKey['Products and accessories'], link: NavigationPathKey.PRODUCT_DASHBOARD},
        {title: TranslationKey['Accessory Search']},
      ]),
    [],
  )

  /**
   * Accessory table columns
   */
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t(TranslationKey.Name),
      flex: 0.8,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.ACCESSORY_VIEW}/${params.row.id}`)),
    },
    {field: 'products', headerName: t(TranslationKey['No. of products']), flex: 0.5},
    {field: 'price', headerName: t(TranslationKey['Default price']), flex: 0.5, headerAlign: 'right', align: 'right'},
    activeCell(t),
  ]

  /**
   * Accessory table visibility model
   */
  const visibilityModel: GridColumnVisibilityModel = {
    company: false,
    products: false,
  }

  /**
   * Create button should only be available to Administrator Users
   */
  const showCreateButton = !isCompanyUser()

  return {navigate, t, showCreateButton, visibilityModel, rows, columns}
}
